import React, { useState, useEffect, useRef } from 'react';
import { Activity, Cpu, HardDrive, Wifi, Settings } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import io from 'socket.io-client';

const PerformanceMonitor = ({ agentId }) => {
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [updateInterval, setUpdateInterval] = useState(5);
  const [performanceData, setPerformanceData] = useState({
    cpu: [],
    memory: [],
    disk: [],
    network: []
  });
  const [currentMetrics, setCurrentMetrics] = useState({
    cpu: 0,
    memory: { used: 0, total: 0, percent: 0 },
    disk: { read: 0, write: 0 },
    network: { sent: 0, received: 0 }
  });
  
  const socketRef = useRef(null);
  const sessionIdRef = useRef(null);
  
  // Calculate max data points for 5-minute sliding window (300 seconds)
  const maxDataPoints = Math.ceil(300 / updateInterval);

  // Simple moving average for smoothing data
  const smoothData = (data, key, windowSize = 3) => {
    if (data.length < windowSize) return data;
    
    return data.map((point, index) => {
      if (index < windowSize - 1) return point;
      
      let sum = 0;
      for (let i = 0; i < windowSize; i++) {
        sum += data[index - i][key] || 0;
      }
      
      return {
        ...point,
        [key]: Math.round((sum / windowSize) * 10) / 10 // Round to 1 decimal
      };
    });
  };

  useEffect(() => {
    // Initialize socket connection
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    socketRef.current = io(backendUrl, {
      path: '/api/socket.io',
      transports: ['websocket'],
      upgrade: false
    });

    socketRef.current.on('connect', () => {
      console.log('✅ Performance monitor connected to WebSocket');
    });

    socketRef.current.on('performance_data', (data) => {
      console.log('📊 Received performance data:', data);
      
      if (data.error) {
        console.error('Performance data error:', data.error);
        return;
      }

      // Update current metrics
      setCurrentMetrics({
        cpu: data.cpu?.usage_percent || 0,
        memory: {
          used: data.memory?.used_gb || 0,
          total: data.memory?.total_gb || 0,
          percent: data.memory?.usage_percent || 0
        },
        disk: {
          read: data.disk?.read_mbps || 0,
          write: data.disk?.write_mbps || 0
        },
        network: {
          sent: data.network?.sent_mbps || 0,
          received: data.network?.received_mbps || 0
        }
      });

      // Add to historical data with rolling time window
      const now = Date.now();
      const timestamp = new Date(data.timestamp).getTime();
      const date = new Date(data.timestamp);
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      const timeLabel = `${minutes}:${seconds}`;

      setPerformanceData(prev => {
        // Add new data points
        const newCpu = { time: timeLabel, value: data.cpu?.usage_percent || 0, timestamp };
        const newMemory = { time: timeLabel, value: data.memory?.usage_percent || 0, timestamp };
        const newDisk = { time: timeLabel, read: data.disk?.read_mbps || 0, write: data.disk?.write_mbps || 0, timestamp };
        const newNetwork = { time: timeLabel, sent: data.network?.sent_mbps || 0, received: data.network?.received_mbps || 0, timestamp };
        
        // Add to arrays and keep only recent data (within 5-minute window)
        const fiveMinutesAgo = now - 300000;
        
        const recentCpu = [...prev.cpu, newCpu].filter(d => d.timestamp >= fiveMinutesAgo);
        const recentMemory = [...prev.memory, newMemory].filter(d => d.timestamp >= fiveMinutesAgo);
        const recentDisk = [...prev.disk, newDisk].filter(d => d.timestamp >= fiveMinutesAgo);
        const recentNetwork = [...prev.network, newNetwork].filter(d => d.timestamp >= fiveMinutesAgo);

        return {
          cpu: recentCpu,
          memory: recentMemory,
          disk: recentDisk,
          network: recentNetwork
        };
      });
    });

    socketRef.current.on('disconnect', () => {
      console.log('❌ Performance monitor disconnected from WebSocket');
    });

    return () => {
      if (socketRef.current) {
        stopMonitoring();
        socketRef.current.disconnect();
      }
    };
  }, []);

  const startMonitoring = () => {
    if (!socketRef.current || !agentId) return;

    sessionIdRef.current = `perf_${Date.now()}`;
    
    socketRef.current.emit('start_performance_monitoring', {
      agent_id: agentId,
      interval: updateInterval,
      session_id: sessionIdRef.current
    }, (response) => {
      if (response?.status === 'ok') {
        console.log('✅ Performance monitoring started');
        setIsMonitoring(true);
      } else {
        console.error('Failed to start monitoring:', response);
      }
    });
  };

  const stopMonitoring = () => {
    if (!socketRef.current || !sessionIdRef.current) return;

    socketRef.current.emit('stop_performance_monitoring', {
      session_id: sessionIdRef.current
    }, (response) => {
      console.log('Performance monitoring stopped');
      setIsMonitoring(false);
      sessionIdRef.current = null;
    });
  };

  const toggleMonitoring = () => {
    if (isMonitoring) {
      stopMonitoring();
    } else {
      startMonitoring();
    }
  };

  // Gauge component for current values
  const Gauge = ({ value, max, label, unit, icon: Icon, color }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const displayValue = typeof value === 'number' ? value.toFixed(1) : '0.0';
    
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${color}`} />
            <span className="font-semibold text-gray-700">{label}</span>
          </div>
          <span className="text-2xl font-bold text-gray-900">
            {displayValue}
            <span className="text-sm text-gray-500 ml-1">{unit}</span>
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full ${color.replace('text-', 'bg-')} transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-700 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(2)} {entry.unit || '%'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Live Performance Monitoring
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Real-time system metrics with 5-minute sliding window
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-gray-500" />
            <label className="text-sm text-gray-700">Update Every:</label>
            <select
              value={updateInterval}
              onChange={(e) => setUpdateInterval(parseInt(e.target.value))}
              disabled={isMonitoring}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value={1}>1 second</option>
              <option value={2}>2 seconds</option>
              <option value={5}>5 seconds</option>
              <option value={10}>10 seconds</option>
            </select>
          </div>
          <button
            onClick={toggleMonitoring}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              isMonitoring
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
          </button>
        </div>
      </div>

      {!isMonitoring && performanceData.cpu.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <Activity className="w-12 h-12 text-blue-500 mx-auto mb-3" />
          <p className="text-blue-800 font-medium">Click "Start Monitoring" to begin live performance tracking</p>
          <p className="text-blue-600 text-sm mt-1">Data will update every {updateInterval} second{updateInterval > 1 ? 's' : ''}</p>
        </div>
      )}

      {(isMonitoring || performanceData.cpu.length > 0) && (
        <>
          {/* Current Metrics Gauges */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Gauge
              value={currentMetrics.cpu}
              max={100}
              label="CPU Usage"
              unit="%"
              icon={Cpu}
              color="text-blue-600"
            />
            <Gauge
              value={currentMetrics.memory.percent}
              max={100}
              label="Memory Usage"
              unit={`% (${currentMetrics.memory.used.toFixed(1)}/${currentMetrics.memory.total.toFixed(1)} GB)`}
              icon={Activity}
              color="text-purple-600"
            />
            <Gauge
              value={currentMetrics.disk.read + currentMetrics.disk.write}
              max={100}
              label="Disk I/O"
              unit={`MB/s (R: ${currentMetrics.disk.read.toFixed(1)} W: ${currentMetrics.disk.write.toFixed(1)})`}
              icon={HardDrive}
              color="text-blue-600"
            />
            <Gauge
              value={currentMetrics.network.sent + currentMetrics.network.received}
              max={100}
              label="Network I/O"
              unit={`MB/s (R: ${currentMetrics.network.received.toFixed(1)} W: ${currentMetrics.network.sent.toFixed(1)})`}
              icon={Wifi}
              color="text-blue-600"
            />
          </div>

          {/* Historical Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CPU Chart */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-blue-600" />
                CPU Usage
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={performanceData.cpu}>
                  <defs>
                    <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    label={{ value: '%', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fill="url(#cpuGradient)"
                    dot={false}
                    name="CPU"
                    unit="%"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Memory Chart */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Activity className="w-5 h-5 text-purple-600" />
                Memory Usage
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={performanceData.memory}>
                  <defs>
                    <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#9333ea" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#9333ea" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12 }}
                    label={{ value: '%', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#9333ea"
                    strokeWidth={2}
                    fill="url(#memoryGradient)"
                    dot={false}
                    name="Memory"
                    unit="%"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Disk I/O Chart */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-blue-600" />
                Disk I/O
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={performanceData.disk}>
                  <defs>
                    <linearGradient id="diskReadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="diskWriteGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    label={{ value: 'MB/s', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="read"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#diskReadGradient)"
                    dot={false}
                    name="Read"
                    unit=" MB/s"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                  <Area
                    type="monotone"
                    dataKey="write"
                    stroke="#f97316"
                    strokeWidth={2}
                    fill="url(#diskWriteGradient)"
                    dot={false}
                    name="Write"
                    unit=" MB/s"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Network I/O Chart */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />
                Network I/O
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={performanceData.network}>
                  <defs>
                    <linearGradient id="networkReadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="networkWriteGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    label={{ value: 'MB/s', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="received"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#networkReadGradient)"
                    dot={false}
                    name="Read"
                    unit=" MB/s"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                  <Area
                    type="monotone"
                    dataKey="sent"
                    stroke="#f97316"
                    strokeWidth={2}
                    fill="url(#networkWriteGradient)"
                    dot={false}
                    name="Write"
                    unit=" MB/s"
                    isAnimationActive={true}
                    animationDuration={300}
                    animationEasing="ease-in-out"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PerformanceMonitor;
